/**
 * Sign Off Modal (Placeholder)
 * =============================
 * Forensic accountant attestation for reconciliation sign-off.
 * TODO: Expand with validation and API integration
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, AlertTriangle, PenLine } from 'lucide-react';
import { useAccountantAuth } from '../../contexts/AccountantAuthContext';
import api from '../../utils/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { FinancialValue } from '../shared/FinancialValue';
import { formatCurrency } from '../../utils/financialFormat';
import CpaWorkpaperTextarea from '../privilege/CpaWorkpaperTextarea';

interface SignOffModalProps {
  reportId: string;
  cellId: string;
  expectedAmount: number;
  actualAmount: number;
  variance: number;
  reclassifiedCount: number;
  onClose: () => void;
  onComplete: () => void;
}

export default function SignOffModal({
  reportId,
  cellId,
  expectedAmount,
  actualAmount,
  variance,
  reclassifiedCount,
  onClose,
  onComplete
}: SignOffModalProps) {
  const { accountant } = useAccountantAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  const [signOffMessage, setSignOffMessage] = useState<string | null>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const variancePercent = expectedAmount !== 0 ? ((Math.abs(variance) / Math.abs(expectedAmount)) * 100).toFixed(1) : '0.0';
  const hasSignificantVariance = Math.abs(variance) > 1000 || parseFloat(variancePercent) > 5;

  const handleSignOff = async () => {
    if (!notes.trim() || notes.trim().length < 10) {
      setError('Please provide notes explaining the variance (minimum 10 characters)');
      return;
    }

    if (!accountant) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(
        `/api/v1/reconciliation/cells/${reportId}/${cellId}/sign-off`,
        {
          notes: notes.trim(),
          variance_accepted: true,
          accountant_name: accountant.full_name,
          accountant_email: accountant.email
        }
      );

      // Success message
      setSignOffMessage(`Reconciliation signed off successfully by ${accountant.full_name}. This attestation has been permanently recorded in the audit trail.`);

      onComplete();
    } catch (err: any) {
      console.error('Error signing off:', err);
      setError(
        err.response?.data?.detail ||
        'Failed to sign off reconciliation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Sign Off Reconciliation" className="rounded-lg shadow-xl max-w-2xl w-full" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}><PenLine size={20} /> Sign Off Reconciliation</h2>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: 'var(--color-text-dim)' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Line Item */}
          <div>
            <label className="text-xs font-bold uppercase" style={{ color: 'var(--color-text-label)' }}>Line Item</label>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-primary)' }}>{cellId}</p>
          </div>

          {/* Summary */}
          <div className="rounded-lg p-4 space-y-2" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <label className="text-xs font-bold uppercase" style={{ color: 'var(--color-text-label)' }}>Reconciliation Summary</label>
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Expected:</span>
              <span className="font-semibold">
                <FinancialValue value={expectedAmount} cents />
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Actual:</span>
              <span className="font-semibold">
                <FinancialValue value={actualAmount} cents />
              </span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Variance:</span>
              <span className="font-bold" style={{ color: Math.abs(variance) < 1 ? 'var(--color-text-primary)' : variance > 0 ? 'var(--color-needs-human-text)' : 'var(--color-verified)' }}>
                {formatCurrency(variance, { cents: true })}
                <span className="text-sm ml-2" style={{ color: 'var(--color-text-muted)' }}>({variancePercent}%)</span>
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Reclassifications:</span>
              <span className="font-semibold">{reclassifiedCount}</span>
            </div>
          </div>

          {/* Variance Warning */}
          {hasSignificantVariance && (
            <div className="border rounded-lg p-4 flex gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <AlertTriangle className="flex-shrink-0" size={20} style={{ color: 'var(--color-brand)' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Significant Variance Detected</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                  This variance exceeds the normal threshold. Please provide detailed notes explaining the discrepancy.
                </p>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="border rounded-lg p-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <p className="text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
            </div>
          )}

          {/* Notes — wrapped per Phase 10 Task 10.5 */}
          <CpaWorkpaperTextarea
            id="signoff-notes"
            label={
              <span className="text-xs font-bold uppercase" style={{ color: 'var(--color-text-label)' }}>
                Notes / Variance Explanation <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
              </span>
            }
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Document variance explanation, reclassification rationale, investigation findings, or any follow-up needed..."
            className="w-full h-32 px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none"
            style={{ borderColor: 'var(--color-border)' }}
            data-testid="signoff-modal-notes"
            helperText={`${notes.length}/10 characters minimum`}
          />

          {/* Warning */}
          {reclassifiedCount > 0 && (
            <div className="border rounded-lg p-3 text-sm flex items-center gap-2" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-text-primary)' }}>
              <AlertTriangle size={14} className="flex-shrink-0" /> Reclassifying will update P&L totals and variance calculations immediately.
            </div>
          )}

          {/* Accountant Info */}
          {accountant && (
            <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <label className="text-xs font-bold uppercase block mb-2" style={{ color: 'var(--color-text-label)' }}>
                Signing Off As
              </label>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
                  {accountant.full_name?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{accountant.full_name}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{accountant.email}</p>
                  {accountant.firm_name && (
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{accountant.firm_name}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded transition-colors disabled:opacity-50"
            style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSignOff}
            disabled={loading || notes.trim().length < 10}
            className="px-4 py-2 text-sm font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
          >
            {loading ? 'Signing Off...' : '✓ Confirm Sign-Off'}
          </button>
        </div>
      </div>
    </div>
  );
}
